import type { ReactNode } from 'react'

export type PlatformId =
  | 'x'
  | 'instagram'
  | 'linkedin'
  | 'facebook'
  | 'threads'
  | 'youtube'
  | 'tiktok'
  | 'bluesky'
  | 'mastodon'

export interface Platform {
  id: PlatformId
  name: string
  color: string // brand color for accents
  gradient: string // css gradient for avatars
  charLimit: number
  icon: ReactNode
}

const S = {
  width: '100%',
  height: '100%',
  viewBox: '0 0 24 24',
  fill: 'currentColor',
  style: { display: 'block' },
} as const

export const PLATFORMS: Record<PlatformId, Platform> = {
  x: {
    id: 'x',
    name: 'X',
    color: '#ffffff',
    gradient: 'linear-gradient(135deg,#2b2b2b,#000)',
    charLimit: 280,
    icon: (
      <svg {...S}>
        <path d="M18.9 2H22l-7.4 8.4L23 22h-6.8l-5.3-6.9L4.8 22H1.7l7.9-9L1 2h7l4.8 6.3L18.9 2Zm-2.4 18h1.9L7.6 3.9H5.6L16.5 20Z" />
      </svg>
    ),
  },
  instagram: {
    id: 'instagram',
    name: 'Instagram',
    color: '#E1306C',
    gradient: 'linear-gradient(135deg,#feda75,#fa7e1e,#d62976,#962fbf,#4f5bd5)',
    charLimit: 2200,
    icon: (
      <svg {...S}>
        <path d="M12 2c2.7 0 3 0 4.1.1 1.1 0 1.8.2 2.4.5.7.2 1.2.6 1.7 1.1.5.5.9 1 1.1 1.7.3.6.5 1.3.5 2.4.1 1.1.1 1.4.1 4.1s0 3-.1 4.1c0 1.1-.2 1.8-.5 2.4a4.8 4.8 0 0 1-1.1 1.7c-.5.5-1 .9-1.7 1.1-.6.3-1.3.5-2.4.5-1.1.1-1.4.1-4.1.1s-3 0-4.1-.1c-1.1 0-1.8-.2-2.4-.5a4.8 4.8 0 0 1-1.7-1.1 4.8 4.8 0 0 1-1.1-1.7c-.3-.6-.5-1.3-.5-2.4C2 15 2 14.7 2 12s0-3 .1-4.1c0-1.1.2-1.8.5-2.4A4.8 4.8 0 0 1 3.7 3.8c.5-.5 1-.9 1.7-1.1.6-.3 1.3-.5 2.4-.5C8.9 2 9.3 2 12 2Zm0 1.8c-2.7 0-3 0-4 .1-1 0-1.5.2-1.9.3-.5.2-.8.4-1.1.7-.3.3-.5.6-.7 1.1-.1.4-.3.9-.3 1.9-.1 1-.1 1.3-.1 4s0 3 .1 4c0 1 .2 1.5.3 1.9.2.5.4.8.7 1.1.3.3.6.5 1.1.7.4.1.9.3 1.9.3 1 .1 1.3.1 4 .1s3 0 4-.1c1 0 1.5-.2 1.9-.3.5-.2.8-.4 1.1-.7.3-.3.5-.6.7-1.1.1-.4.3-.9.3-1.9.1-1 .1-1.3.1-4s0-3-.1-4c0-1-.2-1.5-.3-1.9a3 3 0 0 0-.7-1.1 3 3 0 0 0-1.1-.7c-.4-.1-.9-.3-1.9-.3-1-.1-1.3-.1-4-.1Zm0 3.1a5.1 5.1 0 1 1 0 10.2 5.1 5.1 0 0 1 0-10.2Zm0 1.8a3.3 3.3 0 1 0 0 6.6 3.3 3.3 0 0 0 0-6.6Zm5.3-3.2a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4Z" />
      </svg>
    ),
  },
  linkedin: {
    id: 'linkedin',
    name: 'LinkedIn',
    color: '#0A66C2',
    gradient: 'linear-gradient(135deg,#0a66c2,#004182)',
    charLimit: 3000,
    icon: (
      <svg {...S}>
        <path d="M20.45 20.45h-3.56v-5.57c0-1.33 0-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14ZM7.12 20.45H3.56V9h3.56v11.45ZM22.22 0H1.77C.8 0 0 .78 0 1.74v20.5C0 23.2.8 24 1.78 24h20.44c.98 0 1.78-.8 1.78-1.76V1.74C24 .78 23.2 0 22.22 0Z" />
      </svg>
    ),
  },
  facebook: {
    id: 'facebook',
    name: 'Facebook',
    color: '#1877F2',
    gradient: 'linear-gradient(135deg,#1877f2,#0a4cb3)',
    charLimit: 63206,
    icon: (
      <svg {...S}>
        <path d="M24 12a12 12 0 1 0-13.87 11.85v-8.38H7.08V12h3.05V9.36c0-3 1.79-4.67 4.53-4.67 1.31 0 2.69.24 2.69.24v2.95h-1.52c-1.49 0-1.96.93-1.96 1.88V12h3.33l-.53 3.47h-2.8v8.38A12 12 0 0 0 24 12Z" />
      </svg>
    ),
  },
  threads: {
    id: 'threads',
    name: 'Threads',
    color: '#ffffff',
    gradient: 'linear-gradient(135deg,#333,#000)',
    charLimit: 500,
    icon: (
      <svg {...S}>
        <path d="M17.3 11.2c-.1 0-.2-.1-.3-.1-.2-3.2-1.9-5-4.8-5-1.8 0-3.2.8-4.1 2.1l1.6 1.1c.7-1 1.5-1.3 2.5-1.3 1.5 0 2.6 1 2.8 2.8-.6-.1-1.3-.2-2-.2-2.6 0-4.3 1.4-4.2 3.4.1 1.7 1.5 2.8 3.4 2.8 1.9 0 3.5-1.1 4-3.2.4.6 1 1.1 1 2.2 0 1.9-1.7 4-5.5 4-3.3 0-5.7-2.2-5.7-6.5S7.5 5.5 12 5.5c2.4 0 4.1.7 5.3 2l1.5-1.3C17.7 4.2 15.4 3.3 12 3.3 6.5 3.3 3.4 6.7 3.4 12c0 5.3 3.1 8.7 8.6 8.7 4.9 0 7.7-3 7.7-6.2 0-2-1-3-2.4-3.3Zm-4.9 3.6c-.8 0-1.5-.4-1.5-1.1 0-.8.9-1.2 2-1.2.6 0 1.1.1 1.6.2-.2 1.3-1.1 2.1-2.1 2.1Z" />
      </svg>
    ),
  },
  youtube: {
    id: 'youtube',
    name: 'YouTube',
    color: '#FF0000',
    gradient: 'linear-gradient(135deg,#ff0000,#b30000)',
    charLimit: 5000,
    icon: (
      <svg {...S}>
        <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8ZM9.6 15.6V8.4l6.3 3.6-6.3 3.6Z" />
      </svg>
    ),
  },
  tiktok: {
    id: 'tiktok',
    name: 'TikTok',
    color: '#25F4EE',
    gradient: 'linear-gradient(135deg,#25f4ee,#000,#fe2c55)',
    charLimit: 2200,
    icon: (
      <svg {...S}>
        <path d="M16.6 5.8a4.3 4.3 0 0 1-1-2.8h-3.3v13.4a2.5 2.5 0 1 1-1.8-2.4v-3.4a5.8 5.8 0 1 0 5 5.7V9.6a7.5 7.5 0 0 0 4.4 1.4V7.7a4.3 4.3 0 0 1-3.3-1.9Z" />
      </svg>
    ),
  },
  bluesky: {
    id: 'bluesky',
    name: 'Bluesky',
    color: '#0085FF',
    gradient: 'linear-gradient(135deg,#0085ff,#00c6ff)',
    charLimit: 300,
    icon: (
      <svg {...S}>
        <path d="M5.8 4c2.4 1.8 5 5.5 6.2 7.5C13.2 9.5 15.8 5.8 18.2 4c1.7-1.3 4.3-2.3 4.3.8 0 .6-.4 5-.6 5.7-.7 2.5-3.2 3.1-5.5 2.7 4 .7 5 3 2.8 5.2-4.1 4.3-5.9-1-6.4-2.4-.1-.3-.1-.4-.3-.1-.5 1.4-2.3 6.7-6.4 2.4-2.2-2.3-1.2-4.5 2.8-5.2-2.3.4-4.8-.2-5.5-2.7-.2-.7-.6-5.1-.6-5.7 0-3.1 2.6-2.1 4.3-.8Z" />
      </svg>
    ),
  },
  mastodon: {
    id: 'mastodon',
    name: 'Mastodon',
    color: '#6364FF',
    gradient: 'linear-gradient(135deg,#6364ff,#563acc)',
    charLimit: 500,
    icon: (
      <svg {...S}>
        <path d="M21.3 8.6c0-3.4-2.2-4.4-2.2-4.4C18 3.7 16 3.5 12.1 3.5h-.1C8 3.5 6 3.7 4.9 4.2c0 0-2.2 1-2.2 4.4 0 .8 0 1.7.1 2.8.2 3.5.7 7 4 7.8 1.5.4 2.9.5 3.9.4 1.7-.1 2.6-.6 2.6-.6l-.1-1.3s-1.2.4-2.6.3c-1.4 0-2.8-.1-3.1-1.8 0-.2 0-.3-.1-.5 0 0 1.4.4 3.2.4 1.1 0 2.1-.1 3.2-.2 2.2-.3 4-1.7 4.3-3 .4-2 .3-4.6.3-4.6Zm-3 5h-1.9V8.9c0-1-.4-1.5-1.3-1.5-.9 0-1.4.6-1.4 1.8v2.5h-1.8V9.2c0-1.2-.5-1.8-1.4-1.8-.9 0-1.3.5-1.3 1.5v4.7H5.4V8.7c0-1 .3-1.7.8-2.3.5-.5 1.2-.8 2-.8 1 0 1.7.4 2.2 1.1l.5.8.5-.8c.5-.7 1.2-1.1 2.2-1.1.8 0 1.5.3 2 .8.5.6.8 1.3.8 2.3v4.9Z" />
      </svg>
    ),
  },
}

export const PLATFORM_LIST = Object.values(PLATFORMS)
